import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Copy, MoreHorizontal, Phone, UserCheck, UserX } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { updateClient, type Client } from "../../../lib/clients";
import { Pop, useClosePops } from "../../shell/Pop";
import { useCallContext } from "../../telephony/CallProvider";
import { useToast } from "../../ui";

/**
 * The 44px column at the end of a client row.
 *
 * Only entries that do something. The status toggle is here rather than behind
 * the client page because deactivating somebody is a list-level decision - you
 * notice it while looking at the row next to it, not after opening the record.
 */
export function ClientRowMenu({
  client,
  canManage,
  onChanged,
}: {
  client: Client;
  canManage: boolean;
  onChanged: (next: Client) => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();
  const navigate = useNavigate();
  const call = useCallContext();
  const phone = client.phone ?? client.contacts?.[0]?.phone ?? null;
  const canCall = Boolean(call?.enabled) && Boolean(phone);

  async function copyEmail() {
    close();
    if (!client.email) return;
    try {
      await navigator.clipboard.writeText(client.email);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  async function toggleStatus() {
    close();
    const next = client.status === "active" ? "inactive" : "active";
    try {
      onChanged(await updateClient(client.id, { status: next }));
      toast(t(next === "active" ? "clients.statusActive" : "clients.statusInactive"));
    } catch {
      toast(t("common.error"));
    }
  }

  return (
    // Above the row-covering link overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={`crm-cb${open ? " is-on" : ""}`}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            navigate(`/clients/${client.id}`);
          }}
        >
          <ArrowUpRight className="size-4" />
          {t("clients.open")}
        </button>
        {client.email ? (
          <button type="button" className="crm-mi" onClick={() => void copyEmail()}>
            <Copy className="size-4" />
            {t("clients.copyEmail")}
          </button>
        ) : null}
        {/* The same entry the scanner and the register rows have carried all
            along. A client is a warmer contact than either, and until now it
            was the one the operator had to copy a number out of by hand. The
            primary contact stands in when the company itself has no number:
            the list endpoint already ships that contact with the row. */}
        {canCall ? (
          <button
            type="button"
            className="crm-mi"
            onClick={() => {
              close();
              call?.startCall({
                phone: phone!,
                companyName: client.name,
                regCode: client.regCode ?? undefined,
                clientId: client.id,
                source: "manual",
              });
            }}
          >
            <Phone className="size-4" />
            {t("common.call")}
          </button>
        ) : null}
        {canManage ? (
          <>
            <div className="crm-menu-sep" />
            <button type="button" className="crm-mi" onClick={() => void toggleStatus()}>
              {client.status === "active" ? <UserX className="size-4" /> : <UserCheck className="size-4" />}
              {t(client.status === "active" ? "clients.deactivate" : "clients.activate")}
            </button>
          </>
        ) : null}
      </Pop>
    </span>
  );
}
